import React from 'react';
import { IconArrowRight } from '../icons';
import { Link } from 'react-router-dom';
import SxReveal from './SxReveal';

interface SxShowcaseProps {
  index: string;
  category: string;
  title: string;
  tagline: string;
  description: string;
  highlights: string[];
  /** 有详情页时显示 CTA；产品重构中则留空，避免把访客引向空页面 */
  href?: string;
  ctaLabel?: string;
  statusLabel?: string;
  /** 奇偶交替，形成阅读的左右摆动节奏 */
  reverse?: boolean;
  demo: React.ReactNode;
}

/**
 * 产品能力展示段：左侧叙述 + 右侧模拟界面。
 *
 * 参考站的 5 个能力段全部是这个结构，且严格左右交替。交替不是为了花样，
 * 而是让长页面在滚动时产生可预期的节律——视线落点在左右之间摆动，
 * 比一列到底更不容易疲劳。
 *
 * 移动端强制文字在上、演示在下（忽略 reverse），因为竖屏下"先读后看"
 * 才符合信息获取顺序。
 *
 * 不再接收 brandColor：五个段落各用自己产品的品牌色时，滚动下来会出现
 * 青→橙→紫→蓝→琥珀五种高亮，强调色作为"这里重要"的信号就失效了。
 */
const SxShowcase: React.FC<SxShowcaseProps> = ({
  index,
  category,
  title,
  tagline,
  description,
  highlights,
  href,
  ctaLabel,
  statusLabel,
  reverse = false,
  demo,
}) => (
  <div className="sx-section sx-divide">
    <div className="sx-shell">
      <div className="grid gap-10 md:gap-16 lg:grid-cols-2 lg:items-center">
        {/* 文字列。lg 以下始终排在前面 */}
        <SxReveal className={reverse ? 'lg:order-2' : ''}>
          <div className="flex items-center gap-2.5 mb-5">
            <span className="sx-mono">{index}</span>
            <span className="w-6 h-px" style={{ background: 'var(--sx-line-strong)' }} />
            <span className="sx-mono">{category}</span>
            {statusLabel && (
              <>
                <span className="w-6 h-px" style={{ background: 'var(--sx-line-strong)' }} />
                <span className="sx-mono" style={{ color: 'var(--sx-accent)' }}>
                  {statusLabel}
                </span>
              </>
            )}
          </div>

          <h2 className="sx-h2 mb-3">{title}</h2>
          <p className="sx-h3 mb-5" style={{ color: 'var(--sx-accent)', fontWeight: 400 }}>
            {tagline}
          </p>
          <p className="sx-body" style={{ maxWidth: '54ch' }}>
            {description}
          </p>

          <ul className="flex flex-wrap gap-2 mt-7">
            {highlights.map((h) => (
              <li key={h} className="sx-chip">
                {h}
              </li>
            ))}
          </ul>

          {href && ctaLabel && (
            <div className="mt-8">
              <Link to={href} className="sx-pill sx-pill-ghost group">
                {ctaLabel}
                <IconArrowRight
                  size={13}
                  className="transition-transform duration-300 group-hover:translate-x-0.5"
                />
              </Link>
            </div>
          )}
        </SxReveal>

        {/* 演示列 */}
        <SxReveal delay={0.1} className={reverse ? 'lg:order-1' : ''}>
          {demo}
        </SxReveal>
      </div>
    </div>
  </div>
);

export default SxShowcase;
